import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { evaluationsApi } from '../../../lib/api';
import { toast } from 'sonner';
import {
  Plus, Trash2, Pencil, ClipboardCheck, ChevronLeft, ChevronRight, X, Link2, Shield,
} from 'lucide-react';

function RatingTooltip({ children, text }) {
  const [show, setShow] = useState(false);
  if (!text) return children;

  return (
    <span
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      {children}
      {show && (
        <span style={{
          position: 'absolute',
          bottom: '100%',
          left: 0,
          marginBottom: 8,
          zIndex: 9999,
          width: 280,
          maxWidth: 'calc(100vw - 100px)',
          padding: '10px 14px',
          borderRadius: 6,
          fontSize: 12,
          lineHeight: '1.5',
          fontWeight: 400,
          color: '#fff',
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          pointerEvents: 'none',
          whiteSpace: 'normal',
          textAlign: 'left',
        }}>
          {text}
          <span style={{
            position: 'absolute',
            top: '100%',
            left: 16,
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid rgba(15, 23, 42, 0.95)',
          }} />
        </span>
      )}
    </span>
  );
}

const SECTIONS = [
  { key: 'command', label: 'Command', color: 'bg-gray-600' },
  { key: 'operations', label: 'Operations', color: 'bg-red-600' },
  { key: 'planning', label: 'Planning', color: 'bg-blue-600' },
  { key: 'logistics', label: 'Logistics', color: 'bg-orange-600' },
  { key: 'finance', label: 'Finance', color: 'bg-green-600' },
  { key: 'other', label: 'Other', color: 'bg-purple-600' },
];

const SECTION_ROLES = {
  command: ['IC', 'PIO', 'HRO', 'Liaison Officer', 'Safety Officer'],
  operations: ['Operations Section Chief', 'Staging Area Manager'],
  planning: ['Planning Section Chief', 'Resource Unit', 'Situation Unit', 'Documentation Unit', 'Environment Unit'],
  logistics: ['Logistics Section Chief', 'Service Branch', 'Support Branch'],
  finance: ['Finance Section Chief'],
  other: [],
};

const CAPABILITIES = [
  { name: 'Command Leadership and Execution', high: 'Displayed strategic intent and empowered team', medium: 'Some level of strategic intent, not always fully implemented', low: 'Poor or disconnected leadership, team were not well directed' },
  { name: 'Effective Communication (internal and external)', high: 'Clear, concise and understood by recipient', medium: 'Messages are generally understood but there is some incomplete text or ambiguity', low: 'Unclear, misdirected or poorly delivered messages' },
  { name: 'Management by Objectives', high: 'Clear collaboration with measurable goals and total alignment', medium: 'Clearly stated but lack initial collaboration and feedback', low: 'Dictated without clarity or alignment' },
  { name: 'Span of Control', high: 'Streamlined, structured, and transparent communication channels to keep large, autonomous teams fully aligned without bottlenecking decisions or creating micromanagement', medium: 'Key updates and direction are generally understood across a large team, but limited one-on-one touchpoints lead to occasional delays, minor operational siloing, or missed nuances', low: 'Supervisors/managers fail to provide direct support, leaving large teams isolated, confused by vague directives, and out of alignment' },
  { name: 'Check-in Procedure', high: 'Done correctly and in a timely manner', medium: 'Done, but not immediately', low: 'Not done or required prompt' },
  { name: 'Situational Awareness', high: 'Common operating picture was displayed and well understood and included a structured information management workflow', medium: 'Situation was not always clearly presented or communicated to participants', low: 'No real understanding of current situation and was not readily displayed to team' },
  { name: 'Resource Management', high: '100% status accounting and real-time tracking observed with accurate forecasting. Correct kind and type of resources utilised', medium: 'Status accounting and tracking observed, but not always accurately done. Some incorrect resources were observed in the wrong areas', low: 'Poor accounting and tracking of resources in real time. Wrong types and kinds resourced and not effectively utilised' },
  { name: 'Incident Action Plan Development', high: 'Correctly produced for the NOP, containing the relevant information using the ICS forms applicable. Command approval correctly obtained', medium: 'Partly correct but missing some information. Not all applicable ICS forms were incorporated', low: 'Not produced or not done correctly. Not approved by IC' },
  { name: 'Meeting Preparation and Execution', high: 'Meetings were well prepared, held at the appropriate cadence, attended by the correct personnel, and conducted efficiently. Ground rules were followed', medium: 'Some meetings were not adequately prepared or did not start or finish on time. Key areas were occasionally missed, required personnel were not always present, and ground rules were not consistently followed', low: 'Meetings were poorly prepared, lacked appropriate structure or timing, and did not include the required personnel. Key issues were missed, discussions were inefficient, and ground rules were not followed' },
  { name: 'Demobilisation Procedures', high: 'Demobilisation procedures were clearly planned, communicated, and implemented in a timely manner. Resources were released in the correct order, accountability was maintained, and documentation was completed accurately', medium: 'Demobilisation procedures were completed but not always consistently planned, communicated, or timely. Some resource release, accountability, or documentation issues were observed', low: 'Demobilisation procedures were unclear, poorly coordinated, or not completed correctly. Resources were released without effective accountability, communication, or supporting documentation' },
];

const RATING_COLORS = {
  high: 'bg-emerald-500 text-white',
  medium: 'bg-amber-500 text-white',
  low: 'bg-red-500 text-white',
};

const WIZARD_STEPS = ['Section & Type', 'Roles', 'Link Objective', 'Capabilities'];

export function EvaluationTab({ exerciseId, objectives, isController }) {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [step, setStep] = useState(0);

  const [section, setSection] = useState('');
  const [evalType, setEvalType] = useState('evaluation');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [freeTextRole, setFreeTextRole] = useState('');
  const [linkedObjectiveId, setLinkedObjectiveId] = useState(null);
  const [capabilities, setCapabilities] = useState(
    CAPABILITIES.map(c => ({ name: c.name, rating: null, observation: '' }))
  );

  const loadEvaluations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await evaluationsApi.getAll(exerciseId);
      setEvaluations(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load evaluations'); }
    finally { setLoading(false); }
  }, [exerciseId]);

  useEffect(() => { loadEvaluations(); }, [loadEvaluations]);

  useEffect(() => {
    const handler = () => loadEvaluations();
    window.addEventListener('evaluations:reload', handler);
    return () => window.removeEventListener('evaluations:reload', handler);
  }, [loadEvaluations]);

  const resetWizard = () => {
    setStep(0);
    setSection('');
    setEvalType('evaluation');
    setSelectedRoles([]);
    setFreeTextRole('');
    setLinkedObjectiveId(null);
    setCapabilities(CAPABILITIES.map(c => ({ name: c.name, rating: null, observation: '' })));
    setEditingId(null);
  };

  const openCreate = () => { resetWizard(); setWizardOpen(true); };

  const openEdit = (ev) => {
    setEditingId(ev.evaluation_id);
    setSection(ev.section);
    setEvalType(ev.eval_type);
    setSelectedRoles(ev.roles || []);
    setFreeTextRole(ev.free_text_role || '');
    setLinkedObjectiveId(ev.linked_objective_id || null);
    setCapabilities(
      CAPABILITIES.map(c => {
        const existing = (ev.capabilities || []).find(x => x.name === c.name);
        return existing ? { ...existing } : { name: c.name, rating: null, observation: '' };
      })
    );
    setStep(0);
    setWizardOpen(true);
  };

  const handleSave = async () => {
    if (!section) { toast.error('Select a section'); return; }
    if (selectedRoles.length === 0 && !freeTextRole) { toast.error('Select at least one role'); return; }

    const payload = {
      exercise_id: exerciseId,
      section,
      eval_type: evalType,
      roles: selectedRoles,
      free_text_role: freeTextRole,
      linked_objective_id: linkedObjectiveId,
      capabilities,
    };

    try {
      if (editingId) {
        const { exercise_id, ...updateData } = payload;
        await evaluationsApi.update(editingId, updateData);
        toast.success('Evaluation updated');
      } else {
        await evaluationsApi.create(payload);
        toast.success('Evaluation created');
      }
      setWizardOpen(false);
      resetWizard();
      loadEvaluations();
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    }
  };

  const handleDelete = async (evalId) => {
    if (!window.confirm('Delete this evaluation?')) return;
    try {
      await evaluationsApi.delete(evalId);
      toast.success('Evaluation deleted');
      loadEvaluations();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleRole = (role) => {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const setRating = (idx, rating) => {
    setCapabilities(prev => prev.map((c, i) => i === idx ? { ...c, rating: c.rating === rating ? null : rating } : c));
  };

  const setObservation = (idx, observation) => {
    setCapabilities(prev => prev.map((c, i) => i === idx ? { ...c, observation } : c));
  };

  const sectionMeta = (key) => SECTIONS.find(s => s.key === key) || { label: key, color: 'bg-gray-500' };

  const ratingSummary = (caps) => {
    const h = caps.filter(c => c.rating === 'high').length;
    const m = caps.filter(c => c.rating === 'medium').length;
    const l = caps.filter(c => c.rating === 'low').length;
    const u = caps.filter(c => !c.rating).length;
    const parts = [];
    if (h) parts.push(`${h} High`);
    if (m) parts.push(`${m} Medium`);
    if (l) parts.push(`${l} Low`);
    if (u) parts.push(`${u} Unrated`);
    return parts.join(', ') || 'No ratings';
  };

  const canManage = isController();

  const canGoNext = () => {
    if (step === 0) return !!section;
    if (step === 1) return selectedRoles.length > 0 || freeTextRole.trim().length > 0;
    return true;
  };

  return (
    <Card className="rounded-sm">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-primary" />
            <h3 className="text-base font-heading uppercase font-semibold">Evaluation / Coaching</h3>
            <Badge variant="secondary" className="rounded-sm text-xs">{evaluations.length}</Badge>
          </div>
          {canManage && (
            <Button size="sm" onClick={openCreate} className="rounded-sm" data-testid="create-evaluation-btn">
              <Plus className="w-4 h-4 mr-1" /> Create Evaluation
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : evaluations.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No evaluations yet</p>
            {canManage && <p className="text-xs mt-1">Click "Create Evaluation" to begin</p>}
          </div>
        ) : (
          <div className="space-y-3">
            {evaluations.map(ev => {
              const sm = sectionMeta(ev.section);
              const linkedObj = objectives?.find(o => o.objective_id === ev.linked_objective_id);
              return (
                <div key={ev.evaluation_id} className="border border-border rounded-sm p-4 hover:bg-accent/30 transition-colors" data-testid={`evaluation-${ev.evaluation_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${sm.color}`}>{sm.label}</span>
                        <Badge variant={ev.eval_type === 'coaching' ? 'outline' : 'default'} className="rounded-sm text-xs capitalize">{ev.eval_type}</Badge>
                      </div>
                      <div className="text-sm mb-1">
                        <span className="text-muted-foreground">Roles: </span>
                        <span className="font-medium">
                          {[...(ev.roles || []), ev.free_text_role].filter(Boolean).join(', ') || 'None'}
                        </span>
                      </div>
                      {linkedObj && (
                        <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                          <Link2 className="w-3 h-3" />
                          Objective: {linkedObj.objective_code} — {linkedObj.objective_text?.slice(0, 60)}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">{ratingSummary(ev.capabilities || [])}</div>
                    </div>
                    {canManage && (
                      <div className="flex gap-1 flex-shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(ev)} className="rounded-sm h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(ev.evaluation_id)} className="rounded-sm h-7 w-7 p-0 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Wizard Dialog */}
      <Dialog open={wizardOpen} onOpenChange={(v) => { if (!v) { setWizardOpen(false); resetWizard(); } }}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">
              {editingId ? 'Edit' : 'Create'} Evaluation / Coaching
            </DialogTitle>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center gap-1 mb-4">
            {WIZARD_STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <button
                  onClick={() => { if (i < step) setStep(i); }}
                  className={`text-xs px-2 py-1 rounded ${i === step ? 'bg-primary text-primary-foreground' : i < step ? 'bg-accent text-foreground cursor-pointer' : 'bg-muted text-muted-foreground'}`}
                  disabled={i > step}
                >
                  {i + 1}. {s}
                </button>
                {i < WIZARD_STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
              </React.Fragment>
            ))}
          </div>

          {/* Step 0: Section & Type */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">ICS Section</label>
                <div className="grid grid-cols-3 gap-2">
                  {SECTIONS.map(s => (
                    <button key={s.key} onClick={() => setSection(s.key)}
                      className={`p-3 rounded-sm border text-sm font-medium transition-colors ${section === s.key ? `${s.color} text-white border-transparent` : 'border-border hover:bg-accent'}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Type</label>
                <div className="flex gap-2">
                  {['evaluation', 'coaching'].map(t => (
                    <button key={t} onClick={() => setEvalType(t)}
                      className={`flex-1 p-3 rounded-sm border text-sm font-medium capitalize transition-colors ${evalType === t ? 'bg-primary text-primary-foreground border-transparent' : 'border-border hover:bg-accent'}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Roles */}
          {step === 1 && (
            <div className="space-y-4">
              <label className="text-sm font-medium mb-2 block">
                Select Roles for {sectionMeta(section).label}
              </label>
              {(SECTION_ROLES[section] || []).length > 0 && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 p-2 rounded-sm border border-border hover:bg-accent cursor-pointer font-medium">
                    <input type="checkbox"
                      checked={SECTION_ROLES[section].every(r => selectedRoles.includes(r))}
                      onChange={() => {
                        const allRoles = SECTION_ROLES[section];
                        if (allRoles.every(r => selectedRoles.includes(r))) {
                          setSelectedRoles(selectedRoles.filter(r => !allRoles.includes(r)));
                        } else {
                          setSelectedRoles([...new Set([...selectedRoles, ...allRoles])]);
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm font-medium">All</span>
                  </label>
                  {SECTION_ROLES[section].map(role => (
                    <label key={role} className="flex items-center gap-2 p-2 rounded-sm border border-border hover:bg-accent cursor-pointer">
                      <input type="checkbox" checked={selectedRoles.includes(role)} onChange={() => toggleRole(role)} className="rounded" />
                      <span className="text-sm">{role}</span>
                    </label>
                  ))}
                </div>
              )}
              <div>
                <label className="text-sm font-medium mb-1 block">Free Text Role</label>
                <Input
                  placeholder="Enter a custom role..."
                  value={freeTextRole}
                  onChange={e => setFreeTextRole(e.target.value)}
                  className="rounded-sm"
                />
              </div>
            </div>
          )}

          {/* Step 2: Link Objective */}
          {step === 2 && (
            <div className="space-y-4">
              <label className="text-sm font-medium mb-2 block">Link to Objective (Optional)</label>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <label className="flex items-center gap-2 p-2 rounded-sm border border-border hover:bg-accent cursor-pointer">
                  <input type="radio" name="obj" checked={!linkedObjectiveId} onChange={() => setLinkedObjectiveId(null)} />
                  <span className="text-sm text-muted-foreground">No objective linked</span>
                </label>
                {(objectives || []).map(obj => (
                  <label key={obj.objective_id} className="flex items-center gap-2 p-2 rounded-sm border border-border hover:bg-accent cursor-pointer">
                    <input type="radio" name="obj" checked={linkedObjectiveId === obj.objective_id} onChange={() => setLinkedObjectiveId(obj.objective_id)} />
                    <span className="text-sm">
                      <span className="font-medium">{obj.objective_code}</span> — {obj.objective_text?.slice(0, 80)}
                    </span>
                  </label>
                ))}
              </div>
              {(!objectives || objectives.length === 0) && (
                <p className="text-xs text-muted-foreground">No objectives available for this exercise.</p>
              )}
            </div>
          )}

          {/* Step 3: Capabilities */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">Rate each capability as High, Medium, or Low. Add observations where needed.</p>
              {capabilities.map((cap, idx) => {
                const meta = CAPABILITIES[idx];
                return (
                  <div key={cap.name} className="border border-border rounded-sm p-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{idx + 1}. {cap.name}</p>
                      </div>
                    </div>
                    <div className="flex gap-1.5 mb-2">
                      {['high', 'medium', 'low'].map(r => (
                        <RatingTooltip key={r} text={meta?.[r]}>
                          <button onClick={() => setRating(idx, r)}
                            className={`px-3 py-1 rounded-sm text-xs font-medium transition-colors ${cap.rating === r ? RATING_COLORS[r] : 'border border-border hover:bg-accent'}`}
                          >
                            {r.charAt(0).toUpperCase() + r.slice(1)}
                          </button>
                        </RatingTooltip>
                      ))}
                    </div>
                    {cap.rating && meta && (
                      <p className="text-xs text-muted-foreground mb-2 italic">
                        {meta[cap.rating]}
                      </p>
                    )}
                    <textarea
                      placeholder="Observation..."
                      value={cap.observation}
                      onChange={e => setObservation(idx, e.target.value)}
                      rows={2}
                      className="w-full text-sm border border-border rounded-sm p-2 bg-background resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter className="flex justify-between mt-4">
            <div className="flex gap-2">
              {step > 0 && (
                <Button variant="outline" size="sm" onClick={() => setStep(s => s - 1)} className="rounded-sm">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => { setWizardOpen(false); resetWizard(); }} className="rounded-sm">Cancel</Button>
              {step < WIZARD_STEPS.length - 1 ? (
                <Button size="sm" onClick={() => setStep(s => s + 1)} disabled={!canGoNext()} className="rounded-sm">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button size="sm" onClick={handleSave} className="rounded-sm">
                  {editingId ? 'Update' : 'Save'} Evaluation
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
